const SCENE_VERSION = 1;

const resolveCryptoHost = () => {
  if (typeof window !== 'undefined' && window.crypto) {
    return window.crypto;
  }
  return null;
};

const createOperationId = () => {
  const cryptoHost = resolveCryptoHost();
  if (cryptoHost && typeof cryptoHost.randomUUID === 'function') {
    return cryptoHost.randomUUID();
  }
  return `scene-op-${Date.now()}-${Math.round(Math.random() * 1_000_000)}`;
};

const stampScene = (scene, patch = {}) => ({
  ...scene,
  ...patch,
  revision: Math.max(0, Number(scene?.revision) || 0) + 1,
  updatedAt: new Date().toISOString(),
});

export const cloneEditorScene = (scene) => {
  if (!scene) {
    return null;
  }
  try {
    return JSON.parse(JSON.stringify(scene));
  } catch (_error) {
    return {
      ...scene,
      sourceRgb: scene.sourceRgb ? { ...scene.sourceRgb } : null,
      baseMask: scene.baseMask ? { ...scene.baseMask } : null,
      geometryOps: Array.isArray(scene.geometryOps) ? [...scene.geometryOps] : [],
      maskOps: Array.isArray(scene.maskOps) ? [...scene.maskOps] : [],
      appearanceLayers: Array.isArray(scene.appearanceLayers) ? [...scene.appearanceLayers] : [],
      exportHints: scene.exportHints ? { ...scene.exportHints } : {},
    };
  }
};

export const createEditorScene = ({
  mode = '2d',
  workspaceId = null,
  source = {},
} = {}) => ({
  version: SCENE_VERSION,
  mode,
  revision: 0,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  workspaceId: workspaceId || null,
  sourceRgb: {
    sourceKind: source.sourceKind || 'upload',
    filename: source.filename || null,
    mimeType: source.mimeType || null,
    width: Number(source.width) || null,
    height: Number(source.height) || null,
    preferredArtifact: source.preferredArtifact || null,
    geometryOpCheckpoint: Math.max(0, Number(source.geometryOpCheckpoint) || 0),
    maskOpCheckpoint: Math.max(0, Number(source.maskOpCheckpoint) || 0),
  },
  baseMask: source.baseMask
    ? {
        ...source.baseMask,
        geometryOpCheckpoint: Math.max(0, Number(source.baseMask.geometryOpCheckpoint) || 0),
        maskOpCheckpoint: Math.max(0, Number(source.baseMask.maskOpCheckpoint) || 0),
      }
    : null,
  workingDimensions: {
    width: Number(source.width) || null,
    height: Number(source.height) || null,
  },
  geometryOps: [],
  maskOps: [],
  appearanceLayers: [],
  exportHints: {
    displayOrigin: source.displayOrigin || 'source_upload',
  },
});

export const createSceneOp = ({
  group,
  type,
  payload = {},
  createdAt = null,
}) => ({
  id: createOperationId(),
  group: group || 'geometry',
  type,
  payload,
  createdAt: createdAt || new Date().toISOString(),
});

export const appendSceneOp = (scene, bucket, op) => {
  if (!scene || !bucket || !op) {
    return scene;
  }
  const existing = Array.isArray(scene[bucket]) ? scene[bucket] : [];
  return stampScene(scene, {
    [bucket]: [...existing, op],
  });
};

export const withSceneWorkingDimensions = (scene, { width, height } = {}) => {
  if (!scene) {
    return scene;
  }
  const normalizedWidth = Number(width) || null;
  const normalizedHeight = Number(height) || null;
  const currentWidth = Number(scene.workingDimensions?.width) || null;
  const currentHeight = Number(scene.workingDimensions?.height) || null;
  const sourceWidth = Number(scene.sourceRgb?.width) || null;
  const sourceHeight = Number(scene.sourceRgb?.height) || null;
  const nextSourceWidth = sourceWidth || normalizedWidth;
  const nextSourceHeight = sourceHeight || normalizedHeight;

  if (
    currentWidth === normalizedWidth &&
    currentHeight === normalizedHeight &&
    sourceWidth === nextSourceWidth &&
    sourceHeight === nextSourceHeight
  ) {
    return scene;
  }

  return stampScene(scene, {
    sourceRgb: {
      ...(scene.sourceRgb || {}),
      width: nextSourceWidth,
      height: nextSourceHeight,
    },
    workingDimensions: {
      width: normalizedWidth,
      height: normalizedHeight,
    },
  });
};

export const withSceneSourceArtifact = (scene, preferredArtifact = null, checkpoints = {}) => {
  if (!scene) {
    return scene;
  }
  return stampScene(scene, {
    sourceRgb: {
      ...(scene.sourceRgb || {}),
      preferredArtifact: preferredArtifact || null,
      geometryOpCheckpoint: Math.max(
        0,
        Number(checkpoints.geometryOpCheckpoint ?? scene.sourceRgb?.geometryOpCheckpoint) || 0
      ),
      maskOpCheckpoint: Math.max(
        0,
        Number(checkpoints.maskOpCheckpoint ?? scene.sourceRgb?.maskOpCheckpoint) || 0
      ),
    },
  });
};

export const withSceneBaseMask = (scene, baseMask = null) => {
  if (!scene) {
    return scene;
  }
  return stampScene(scene, {
    baseMask: baseMask || null,
  });
};

export const withSceneDisplayOrigin = (scene, displayOrigin, details = {}) => {
  if (!scene) {
    return scene;
  }
  return stampScene(scene, {
    exportHints: {
      ...(scene.exportHints || {}),
      displayOrigin: displayOrigin || (scene.exportHints && scene.exportHints.displayOrigin) || 'source_upload',
      ...details,
    },
  });
};

export const withSceneExportHints = (scene, details = {}) => {
  if (!scene) {
    return scene;
  }
  const currentDisplayOrigin =
    (scene.exportHints && scene.exportHints.displayOrigin) || 'source_upload';
  return stampScene(scene, {
    exportHints: {
      ...(scene.exportHints || {}),
      displayOrigin: currentDisplayOrigin,
      ...details,
    },
  });
};

export const syncTextLayersToScene = (scene, textLayers = []) => {
  if (!scene) {
    return scene;
  }
  const appearanceLayers = Array.isArray(textLayers)
    ? textLayers.map((layer) => ({
        id: layer.id || createOperationId(),
        type: 'text',
        text: layer.text || '',
        fontFamily: layer.fontFamily || 'Inter',
        fontSize: Number(layer.fontSize) || 0,
        fontWeight: Number(layer.fontWeight) || 0,
        fill: layer.fill || '#111827',
        align: layer.align || 'left',
        rotation: Number(layer.rotation) || 0,
        x: Number(layer.x) || 0,
        y: Number(layer.y) || 0,
        space: layer.space || 'image',
        fontMm: Number(layer.fontMm) || 0,
      }))
    : [];

  return stampScene(scene, { appearanceLayers });
};
